import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Config } from '../config';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';
import { useAuth } from '../providers/AuthProvider';
import Toast from '../components/Toast';
import { authApi } from '../services/api';

export default function LoginPage() {
  const labels = useLabels();
  const t = useT();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [step2fa, setStep2fa] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [code2fa, setCode2fa] = useState('');
  const [code2faVisibleUntil, setCode2faVisibleUntil] = useState([]);
  const [maskRefresh, setMaskRefresh] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken } = useAuth();

  useEffect(() => {
    // Check for success message from navigation state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the state to prevent showing it again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const finishLogin = (responseData) => {
    const tokenData = responseData.token;
    setToken(tokenData.cii_access_token);
    if (responseData.userinfo) {
      localStorage.setItem('userinfo', JSON.stringify(responseData.userinfo));
      const resolvedRole = responseData.role || responseData.userinfo?.role || tokenData?.role || 'user';
      localStorage.setItem('role', resolvedRole);
      if (responseData.userinfo.avatar) {
        localStorage.setItem('user_avatar', responseData.userinfo.avatar);
      }
    }
    if (responseData.requires_2fa_setup) {
      localStorage.setItem('requires_2fa_setup', '1');
      navigate('/profile', { state: { tab: 'security' } });
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${Config.APIBaseURL}/auth/login`, credentials);

      if (response.data.requires_2fa && response.data.temp_token) {
        setTempToken(response.data.temp_token);
        setStep2fa(true);
        setCode2fa('');
        setCode2faVisibleUntil([]);
        setError('');
      } else if (response.data.token) {
        finishLogin(response.data);
      } else if (response.data.result) {
        setError(typeof response.data.result === 'string' ? response.data.result : 'Login failed.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.verify2fa(tempToken, code2fa);
      if (res.data && res.data.token) {
        finishLogin(res.data);
      } else {
        setError(t('auth.invalidCode'));
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const code2faDisplay = (() => {
    const now = Date.now();
    let s = '';
    for (let i = 0; i < 6; i++) {
      if (i >= code2fa.length) break;
      const until = code2faVisibleUntil[i] || 0;
      s += now < until ? code2fa[i] : '*';
    }
    return s;
  })();

  const handle2faCodeKeyDown = (e) => {
    if (e.key >= '0' && e.key <= '9' && code2fa.length < 6) {
      e.preventDefault();
      const idx = code2fa.length;
      setCode2fa((prev) => prev + e.key);
      setCode2faVisibleUntil((prev) => [...prev.slice(0, idx), Date.now() + 500]);
      setTimeout(() => setMaskRefresh((r) => r + 1), 500);
    } else if (e.key === 'Backspace' && code2fa.length > 0) {
      e.preventDefault();
      setCode2fa((prev) => prev.slice(0, -1));
      setCode2faVisibleUntil((prev) => prev.slice(0, -1));
    }
  };

  return (
    <div className="min-h-screen bg-eucnet-gradient flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <Toast
        open={!!successMessage}
        setOpen={(open) => !open && setSuccessMessage('')}
        type="success"
        className="fixed top-6 right-6 z-50"
      >
        {successMessage}
      </Toast>
      
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center items-center mb-0 mt-6" style={{ height: 110 }}>
            <img 
              src={labels.loginLogo || Config.loginLogo}
              alt="Logo" 
              className="w-auto object-contain object-center"
              style={{ maxHeight: 100, height: 100 }}
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-white tracking-tight">
            {labels.platformTitle}
          </h2>
          <p className="mt-3 text-center text-2xl text-white/90 font-medium">
            {labels.platformSubtitle}
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          {!step2fa ? (
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-eucnet-navy mb-2">
                {t('auth.username')}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                placeholder={t('auth.enterUsername')}
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-eucnet-navy mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="block w-full px-4 py-3 pr-10 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                  placeholder={t('auth.enterPassword')}
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 z-10"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-4 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-base font-semibold text-white bg-gradient-to-r from-eucnet-primary to-eucnet-sky hover:from-eucnet-navy hover:to-eucnet-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eucnet-sky disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
              >
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </button>
            </div>

            <div className="flex items-center justify-center text-sm pt-2">
              <Link to="/forgot-password" className="font-medium text-eucnet-blue hover:text-eucnet-sky transition-colors">
                {t('auth.forgotPassword')}
              </Link>
            </div>
          </form>
          ) : (
          <form className="space-y-6" onSubmit={handleVerify2fa}>
            <p className="text-sm text-eucnet-grey-600">{t('auth.twoFactorPrompt')}</p>
            <div>
              <label htmlFor="code2fa" className="block text-sm font-semibold text-eucnet-navy mb-2">
                {t('auth.verificationCode')}
              </label>
              <input
                id="code2fa"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code2faDisplay}
                onKeyDown={handle2faCodeKeyDown}
                onChange={() => {}}
                className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky text-center text-lg tracking-widest"
              />
            </div>
            {error && (
              <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-4 rounded-lg">
                {error}
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setStep2fa(false); setTempToken(''); setCode2fa(''); setCode2faVisibleUntil([]); setError(''); }}
                className="flex-1 py-3 px-4 border border-eucnet-grey-300 rounded-lg text-eucnet-navy font-medium hover:bg-eucnet-grey-100"
              >
                {t('common.back')}
              </button>
              <button
                type="submit"
                disabled={loading || code2fa.length !== 6}
                className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-base font-semibold text-white bg-gradient-to-r from-eucnet-primary to-eucnet-sky hover:from-eucnet-navy hover:to-eucnet-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eucnet-sky disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.verifying') : t('auth.verify')}
              </button>
            </div>
          </form>
          )}
        </div>

        <p className="text-center text-sm text-white/80 font-medium">
          © 2026 {labels.brandName}. {t('common.allRightsReserved')} <span className="text-white/50">v2.0.6</span>
        </p>
      </div>
    </div>
  );
}
